import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { format } from "date-fns";
import {
  ArrowLeft,
  Award,
  Calendar,
  CheckCircle,
  Clock,
  ExternalLink,
  Flag,
  HelpCircle,
  Sparkles,
  Target,
  TrendingUp,
  Trophy,
  XCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import ClientImage from "@/components/ui/ClientImage";
import DownloadReportButton from "@/components/report/DownloadReportButton";
import CopyButton from "@/components/report/CopyButton";
import PieChart from "@/components/analytics/PieChart";
import BarChart from "@/components/analytics/BarChart";
import LineChart from "@/components/analytics/LineChart";

import { ReportServices } from "@/lib/services/report";
import type { TReportDetailItem } from "@/types/reports";

export default function ReportDetailPage() {
  const { report_id } = useParams<{ report_id: string }>();
  const [data, setData] = useState<TReportDetailItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!report_id) {
      setError("Missing report id");
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);
    setError(null);

    ReportServices.getReportById(report_id)
      .then((res) => {
        if (!mounted) return;
        setData(res);
      })
      .catch((e: unknown) => {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : "Failed to load report");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [report_id]);

  const computed = useMemo(() => {
    const obtained = Number(data?.total_obtained_score ?? 0);
    const total = Number(data?.total_score ?? 0);
    const remaining = Math.max(0, total - obtained);

    const safePercent = (num: number, denom: number, fallback = 0) => {
      const n = Number(num ?? 0);
      const d = Number(denom ?? 0);
      if (!isFinite(n) || !isFinite(d) || d <= 0) return fallback;
      return (n / d) * 100;
    };

    const completionPercent = Math.round(safePercent(obtained, total, 0));
    const obtainedPercent = Math.round(safePercent(obtained, total, 0));
    const remainingPercent = Math.max(0, 100 - obtainedPercent);

    const flags = data?.flags ?? [];
    const flagValues = flags.map((f) => Number(f.participant_answers?.obtained_score ?? 0));
    const flagLabels = flags.map(
      (f, i) => `${i + 1}. ${String(f.question || "").slice(0, 24)}`,
    );
    const hintUsedCount = flags.filter((f) => f.participant_answers?.hint_used).length;
    const correctCount = flagValues.filter((v) => v > 0).length;
    const flagsCount = flags.length;
    const accuracyPercent = flagsCount > 0 ? Math.round((correctCount / flagsCount) * 100) : 0;

    return {
      obtained,
      total,
      remaining,
      completionPercent,
      obtainedPercent,
      remainingPercent,
      flagValues,
      flagLabels,
      hintUsedCount,
      correctCount,
      flagsCount,
      accuracyPercent,
      safePercent,
    };
  }, [data]);

  const sc = (data?.scenario ?? {}) as Record<string, unknown>;
  const scenarioId = typeof sc.id === "string" ? sc.id : undefined;
  const scenarioName = typeof sc.name === "string" ? sc.name : undefined;
  const scenarioType = typeof sc.type === "string" ? sc.type : undefined;
  const scenarioCategory = typeof sc.category_name === "string" ? sc.category_name : undefined;
  const scenarioSeverity =
    (typeof sc.severity === "string" ? sc.severity : undefined) ||
    (typeof sc.difficulty === "string" ? sc.difficulty : undefined);
  const scenarioDescription = typeof sc.description === "string" ? sc.description : undefined;
  const scenarioThumbnail = typeof sc.thumbnail_url === "string" ? sc.thumbnail_url : undefined;
  const scenarioDuration = typeof sc.duration === "number" ? sc.duration : undefined;
  const scenarioTags = Array.isArray(sc.tags) ? (sc.tags as unknown[]).filter((t): t is string => typeof t === "string") : [];

  return (
    <div
      id="report-content"
      className="flex flex-col mx-auto px-3.5 sm:px-7 lg:px-7 py-5 md:py-7 space-y-5 md:space-y-7 min-h-screen bg-background"
    >
      {loading ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Loading report…</p>
          </CardContent>
        </Card>
      ) : error ? (
        <Card className="border-destructive/30">
          <CardContent className="p-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      ) : !data ? (
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Report not found.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5 md:space-y-7">
          {/* Header */}
          <div className="mb-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center shadow-lg">
                  <Trophy className="h-6 w-6 text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
                    Performance Report
                  </h1>
                  <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                    <Award className="h-4 w-4" />
                    <span className="truncate max-w-[200px] sm:max-w-none">
                      {data.user?.user_full_name ?? "User"}
                    </span>
                    {scenarioName ? (
                      <>
                        <span>•</span>
                        <span className="truncate max-w-[300px]">{scenarioName}</span>
                      </>
                    ) : null}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              <DownloadReportButton reportId={data.id} reportData={data} />
              <Link to="/players" className="w-full sm:w-auto">
                <Button variant="outline" size="sm" className="gap-2 hover:bg-accent transition-all w-full sm:w-auto">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Players
                </Button>
              </Link>
            </div>
          </div>

          {/* Scenario details */}
          <Card className="overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 border border-border bg-card">
            <div className="md:flex">
              <div className="md:w-1/3 relative group">
                {scenarioThumbnail ? (
                  <div className="h-full w-full overflow-hidden">
                    <ClientImage
                      src={scenarioThumbnail}
                      alt={scenarioName || "Scenario"}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-110"
                      fallbackSrc="/images/placeholder.png"
                    />
                  </div>
                ) : (
                  <div className="h-48 md:h-full bg-gradient-to-br from-muted/20 to-muted/40 flex items-center justify-center">
                    <div className="text-center p-4">
                      <Flag className="h-12 w-12 mx-auto text-primary/60 mb-3 animate-pulse" />
                      <p className="text-sm text-muted-foreground font-medium">No thumbnail available</p>
                    </div>
                  </div>
                )}

                <div className="absolute top-4 left-4">
                  <Badge
                    variant="secondary"
                    className="backdrop-blur-md shadow-lg font-semibold px-3 py-1.5 border border-border"
                  >
                    {scenarioSeverity ?? "Intermediate"}
                  </Badge>
                </div>
              </div>

              <div className="p-5 md:w-2/3 bg-card">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h2 className="text-3xl font-bold tracking-tight text-foreground">
                      {scenarioName || "Unnamed Scenario"}
                    </h2>
                    <p className="text-muted-foreground mt-2 leading-relaxed">
                      {scenarioDescription || "No description available"}
                    </p>

                    <div className="mt-5 grid grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                          Category
                        </p>
                        <p className="font-semibold text-lg flex items-center gap-2 text-foreground">
                          <Target className="h-4 w-4 text-primary" />
                          {scenarioCategory || "N/A"}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">Type</p>
                        <p className="font-semibold text-lg text-foreground">{scenarioType || "N/A"}</p>
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                          Duration
                        </p>
                        <p className="font-semibold text-lg flex items-center gap-2 text-foreground">
                          <Clock className="h-4 w-4 text-warning" />
                          {scenarioDuration ? `${scenarioDuration} min` : "N/A"}
                        </p>
                      </div>

                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                          Max Score
                        </p>
                        <p className="font-semibold text-lg flex items-center gap-2 text-foreground">
                          <Trophy className="h-4 w-4 text-primary" />
                          {computed.total} pts
                        </p>
                      </div>
                    </div>

                    {scenarioTags.length ? (
                      <div className="mt-5">
                        <p className="text-xs text-muted-foreground mb-3 uppercase tracking-wider font-semibold">
                          Tags
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {scenarioTags.map((tag, i) => (
                            <Badge
                              key={`${tag}-${i}`}
                              variant="outline"
                              className="text-xs px-3 py-1 bg-muted text-muted-foreground border-border hover:bg-muted/90 transition-all"
                            >
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div className="text-right md:ml-5 ml-0 mt-5 md:mt-0">
                    <div className="rounded-2xl p-5 shadow-xl bg-gradient-to-br from-success to-accent text-success-foreground">
                      <div className="text-4xl font-bold text-card-foreground">
                        {computed.obtained}
                        <span className="text-xl opacity-90">/{computed.total}</span>
                      </div>
                      <div className="text-xs uppercase tracking-wide opacity-95 font-semibold mt-1 text-card-foreground/90">
                        Points Earned
                      </div>
                      <div className="mt-4">
                        <div className="h-3 w-32 bg-muted/20 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-primary to-primary/80 rounded-full transition-all duration-500 shadow-lg"
                            style={{ width: `${computed.completionPercent}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-sm font-bold mt-2 flex items-center justify-end gap-1">
                        <TrendingUp className="h-4 w-4" />
                        {computed.completionPercent}% Complete
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 pt-5 border-t border-border/50 flex items-center justify-between">
                  <div className="text-sm space-y-2">
                    <p className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-primary" />
                      <span className="text-muted-foreground">Started:</span>
                      <span className="text-foreground font-medium">
                        {data.start_time ? format(new Date(data.start_time), "MMM d, yyyy h:mm a") : "N/A"}
                      </span>
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-emerald-500" />
                      <span className="text-muted-foreground">Completed:</span>
                      <span className="text-foreground font-medium">
                        {data.end_time ? format(new Date(data.end_time), "MMM d, yyyy h:mm a") : "In Progress"}
                      </span>
                    </p>
                  </div>

                  <Button variant="default" size="sm" asChild className="gap-2 shadow-lg">
                    <Link to={scenarioId ? `/scenarios/${scenarioId}/detail` : "/scenarios"}>
                      <ExternalLink className="h-4 w-4" />
                      View Scenario
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Analytics */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 md:gap-7">
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              <Card className="overflow-hidden shadow-lg hover:shadow-xl transition-all border border-border bg-card">
                <CardHeader className="pb-4 bg-muted">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl flex items-center gap-2 text-foreground">
                        <TrendingUp className="h-6 w-6 text-emerald-600" />
                        Performance Overview
                      </CardTitle>
                      <CardDescription className="mt-1 text-muted-foreground">
                        Your progress and score distribution across all flags
                      </CardDescription>
                    </div>
                    <div className="hidden md:flex items-center gap-3 text-sm">
                      <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50">
                        <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                        <span className="font-medium text-emerald-700">Correct (Score &gt; 0)</span>
                      </span>
                      <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50">
                        <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                        <span className="font-medium text-red-700">Missed (Score = 0)</span>
                      </span>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-80 p-4 bg-card rounded-lg border border-border">
                    <BarChart
                      data={computed.flagValues}
                      width={800}
                      height={300}
                      labels={computed.flagLabels}
                      showValues
                      showGrid
                      colorByValue
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg hover:shadow-xl transition-all border border-border bg-card">
                <CardHeader className="bg-muted">
                  <CardTitle className="text-2xl flex items-center gap-2 text-foreground">
                    <TrendingUp className="h-6 w-6 text-primary" />
                    Score Timeline
                  </CardTitle>
                  <CardDescription className="mt-1 text-muted-foreground">
                    How your score progressed over time
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="h-64 p-4 bg-card rounded-lg border border-border">
                    <LineChart
                      data={computed.flagValues}
                      width={800}
                      height={224}
                      showArea
                      showGrid
                      showPoints
                      showValues={false}
                      color="#3b82f6"
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-5">
              <Card className="shadow-lg hover:shadow-xl transition-all border border-border bg-card">
                <CardHeader className="bg-muted">
                  <CardTitle className="text-xl flex items-center gap-2 text-foreground">
                    <Trophy className="h-5 w-5 text-primary" />
                    Score Distribution
                  </CardTitle>
                  <CardDescription className="mt-1 text-muted-foreground">How your score is divided</CardDescription>
                </CardHeader>
                <CardContent className="p-5">
                  <div className="flex flex-col gap-5 md:gap-3">
                    <div className="flex-1 flex items-center justify-center min-h-[225px]">
                      <PieChart
                        data={[
                          { label: "Obtained", value: computed.obtainedPercent, color: "#10b981" },
                          { label: "Remaining", value: computed.remainingPercent, color: "#e5e7eb" },
                          ...(computed.hintUsedCount > 0
                            ? [{ label: "Hints Used", value: computed.hintUsedCount, color: "#f59e0b" }]
                            : []),
                        ]}
                        width={240}
                        height={240}
                        innerRadius={60}
                        showLabels
                        legend={false}
                      />
                    </div>

                    <div className="flex-1 space-y-2.5">
                      <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-emerald-500 ring-2 ring-emerald-200/50 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Correct</p>
                            <p className="text-xs text-muted-foreground">Points obtained</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-foreground">{computed.obtained} pts</p>
                          <p className="text-xs text-emerald-500 font-medium">
                            {computed.total > 0 ? computed.completionPercent : 0}% of total
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border">
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-muted-foreground/30 ring-2 ring-muted/20 flex-shrink-0" />
                          <div>
                            <p className="font-medium text-foreground">Remaining</p>
                            <p className="text-xs text-muted-foreground">Points available</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-foreground">{computed.remaining} pts</p>
                          <p className="text-xs text-muted-foreground">
                            {computed.total > 0 ? computed.remainingPercent : 0}% of total
                          </p>
                        </div>
                      </div>

                      {computed.hintUsedCount > 0 ? (
                        <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-amber-500/20">
                          <div className="flex items-center gap-3">
                            <Sparkles className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-foreground">Hints Used</p>
                              <p className="text-xs text-muted-foreground">Total hints used</p>
                            </div>
                          </div>
                          <span className="font-bold text-foreground">{computed.hintUsedCount}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="shadow-lg hover:shadow-xl transition-all border border-border bg-card">
                <CardHeader className="bg-muted">
                  <CardTitle className="text-xl flex items-center gap-2 text-foreground">
                    <Award className="h-5 w-5 text-primary" />
                    Performance Metrics
                  </CardTitle>
                  <CardDescription className="mt-1 text-muted-foreground">Key statistics about your performance</CardDescription>
                </CardHeader>
                <CardContent className="space-y-5 p-5">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground font-semibold">Accuracy</span>
                      <span className="font-bold text-lg text-foreground">{computed.accuracyPercent}%</span>
                    </div>
                    <div className="h-3 w-full bg-muted rounded-full overflow-hidden shadow-inner">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500 shadow-md"
                        style={{ width: `${(computed.correctCount / (computed.flagsCount || 1)) * 100}%` }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground font-semibold">Completion</span>
                      <span className="font-bold text-lg text-foreground">{computed.completionPercent}%</span>
                    </div>
                    <div className="h-3 w-full bg-muted rounded-full overflow-hidden shadow-inner">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500 shadow-md"
                        style={{ width: `${computed.completionPercent}%` }}
                      />
                    </div>
                  </div>

                  <div className="pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="text-center p-4 rounded-xl bg-muted border border-border">
                        <div className="text-3xl font-bold text-foreground">{computed.correctCount}</div>
                        <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mt-1">
                          Correct
                        </div>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-muted border border-border">
                        <div className="text-3xl font-bold text-foreground">
                          {computed.flagsCount - computed.correctCount}
                        </div>
                        <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mt-1">
                          Missed
                        </div>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-muted border border-border">
                        <div className="text-3xl font-bold text-foreground">{computed.hintUsedCount}</div>
                        <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mt-1">
                          Hints Used
                        </div>
                      </div>
                      <div className="text-center p-4 rounded-xl bg-muted border border-border">
                        <div className="text-3xl font-bold text-foreground">
                          {computed.flagsCount > 0 ? `${computed.accuracyPercent}%` : "N/A"}
                        </div>
                        <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mt-1">
                          Success Rate
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Flags section */}
          <div className="space-y-6">
            <div className="flex items-center justify-between p-5 rounded-xl bg-muted border border-border shadow-sm">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2 text-foreground">
                  <Flag className="h-6 w-6 text-primary" />
                  Challenge Details
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Review your performance on each challenge
                </p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-blue-600">{computed.correctCount}/{computed.flagsCount}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">Completed</div>
              </div>
            </div>

            <div className="space-y-2">
              {data.flags && data.flags.length > 0 ? (
                data.flags.map((f) => {
                  const flagObtained = Number(f.participant_answers?.obtained_score ?? 0);
                  const earned = flagObtained > 0;
                  const maxScore = Number(f.score) || 1;
                  const scorePercentage = Math.round(computed.safePercent(flagObtained, maxScore, 0));

                  return (
                    <Card
                      key={f.id}
                      className="overflow-hidden hover:shadow-md transition-all duration-200 border-l-4 border-l-success hover:border-l-primary bg-card border border-border"
                    >
                      <details className="group">
                        <summary className="cursor-pointer list-none hover:bg-muted/30 transition-colors">
                          <div className="p-4">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div
                                  className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                                    earned ? "bg-success text-success-foreground" : "bg-destructive text-destructive-foreground"
                                  }`}
                                >
                                  {earned ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                                </div>

                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-medium text-sm text-foreground leading-tight">
                                      {f.question}
                                    </h3>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      {f.participant_answers?.hint_used ? (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Badge variant="outline" className="text-xs px-2 py-0.5 bg-muted border border-border text-muted-foreground">
                                                <Sparkles className="h-3 w-3 mr-1" />
                                                Hint
                                              </Badge>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p>Hint was used</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      ) : null}
                                      <Badge variant={earned ? "default" : "destructive"} className="text-xs px-2 py-0.5">
                                        {flagObtained}/{f.score}
                                      </Badge>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1">
                                      <Flag className="h-3 w-3" />
                                      {f.team || "N/A"}
                                    </span>
                                    <span>•</span>
                                    <span className="font-medium">{scorePercentage.toFixed(0)}% Complete</span>
                                    <span>•</span>
                                    <span className="text-muted-foreground">
                                      {f.participant_answers?.retries ?? 0} attempts
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-16 rounded-full overflow-hidden bg-muted">
                                  <div
                                    style={{ width: `${scorePercentage}%` }}
                                    className={`h-full transition-all duration-500 ${earned ? "bg-primary" : "bg-destructive"}`}
                                  />
                                </div>
                                <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center transition-colors">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="transition-transform duration-300 text-muted-foreground group-open:rotate-180"
                                  >
                                    <path d="m6 9 6 6 6-6" />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>
                        </summary>

                        <Separator className="my-0" />

                        <div className="p-4 bg-muted">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-xs font-medium mb-2 text-muted-foreground flex items-center gap-1">
                                <Flag className="h-3 w-3" />
                                Your Submission
                              </h4>
                              <div className="bg-card rounded border border-border p-3 relative shadow-sm">
                                <div className="font-mono text-sm break-words pr-8 text-foreground">
                                  {f.participant_answers?.submitted_response || (
                                    <span className="text-muted-foreground italic">No submission</span>
                                  )}
                                </div>
                                {f.participant_answers?.submitted_response ? (
                                  <div className="absolute top-2 right-2">
                                    <CopyButton text={f.participant_answers.submitted_response} />
                                  </div>
                                ) : null}
                              </div>
                            </div>

                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                                  <Trophy className="h-3 w-3" />
                                  Attempt Details
                                </h4>
                                <span className="text-xs text-muted-foreground">
                                  {f.participant_answers?.updated_at
                                    ? format(new Date(f.participant_answers.updated_at), "MMM d, h:mm a")
                                    : "N/A"}
                                </span>
                              </div>

                              <div className="bg-card rounded border border-border p-3 space-y-2 text-sm shadow-sm">
                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Status:</span>
                                  <Badge variant={earned ? "default" : "destructive"} className="text-xs">
                                    {earned ? "Complete" : "Incomplete"}
                                  </Badge>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Attempts:</span>
                                  <span className="font-medium text-foreground">{f.participant_answers?.retries ?? 0}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                  <span className="text-muted-foreground">Points:</span>
                                  <span className="font-medium text-foreground">
                                    {flagObtained} / {f.score}
                                  </span>
                                </div>
                                {f.participant_answers?.hint_used ? (
                                  <div className="flex justify-between items-center pt-2 border-t border-border">
                                    <span className="text-muted-foreground flex items-center gap-1">
                                      <Sparkles className="h-3 w-3 text-warning" />
                                      Hint Used
                                    </span>
                                    <Badge variant="outline" className="bg-muted border border-border text-muted-foreground text-xs">
                                      Yes
                                    </Badge>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>

                          {f.participant_answers?.feedback ? (
                            <div className="mt-4 p-3 bg-muted rounded border border-border">
                              <h4 className="text-xs font-medium text-foreground mb-1 flex items-center gap-1">
                                <HelpCircle className="h-3 w-3" />
                                Feedback
                              </h4>
                              <p className="text-sm text-muted-foreground">{f.participant_answers.feedback}</p>
                            </div>
                          ) : null}
                        </div>
                      </details>
                    </Card>
                  );
                })
              ) : (
                <Card className="shadow-lg border border-border bg-card">
                  <CardContent className="p-12 text-center">
                    <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-2xl bg-muted">
                      <Flag className="h-10 w-10 text-muted-foreground animate-pulse" />
                    </div>
                    <h3 className="mt-6 text-xl font-bold text-foreground">No challenges recorded</h3>
                    <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">
                      There are no challenge attempts to display for this report.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="flex justify-between items-center pt-6 border-t border-border">
              <Button variant="outline" size="sm" asChild className="gap-2 hover:bg-accent transition-all">
                <Link to="/scenarios">
                  <ArrowLeft className="h-4 w-4" />
                  Back to Scenarios
                </Link>
              </Button>
              <div className="text-sm text-muted-foreground">
                Report ID: <span className="font-mono font-semibold text-foreground">{data.id}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
